import { yupResolver } from "@hookform/resolvers/yup";
import { InputAdornment } from "@mui/material";
import dayjs from "dayjs";
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { useUploadFileMutation } from "../../../../../../../../api/services/client/clientApis";
import { FileUploadResponse } from "../../../../../../../../api/services/client/models";
import { useProgramUploadDocumentDetailsMutation } from "../../../../../../../../api/services/program/programApis";
import { ReactComponent as CalendarIcon } from "../../../../../../../../assets/images/calendar-icon.svg";
import {
  AccordionHeader,
  AccordionHeaderText,
  FormController,
  MuiBox,
  MuiGrid,
  MuiModal,
  MuiSelect,
  MuiTextField,
  WarningConfirmationModal,
} from "../../../../../../../../components";
import FileUploader from "../../../../../../../../components/fileUpload/FileUpload";
import {
  MAX_LENGTH_500,
  PAGE_IDS,
} from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import { commonSiteCoreKeys } from "../../../../../../../../helpers/constants/siteCoreKeys";
import {
  getAllFileTypes,
  getFieldUndefinedForPayloadParams,
  getValueOrBlank,
  isEmpty,
} from "../../../../../../../../helpers/utils";
import { useGridContainer } from "../../../../../../../../hooks/useGridContainer";
import { useMediaQueries } from "../../../../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import { programUploadNewDocumentSitecoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { UploadNewDocumentFormKey } from "../../models/Models";
import { UploadNewDocumentModalValidationSchema } from "../../models/ValidationSchema";
import { UploadNewDocumentModalWrapper } from "./UploadNewDocumentModal.style";

interface UploadNewDocumentModalProps {
  open: boolean;
  onClose: () => void;
  programId: string;
  onDocumentUploadSuccess: () => void;
}

const pageId = PAGE_IDS.PROGRAM_UPLOAD_DOCUMENT;
export const UploadNewDocumentModal: FC<UploadNewDocumentModalProps> = ({
  open,
  onClose,
  programId,
  onDocumentUploadSuccess,
}) => {
  const {
    useFetchLabel,
    commonLabels,
    fetchComponentTitle,
    commonValidationMessages,
    fetchDropdownValues,
    fetchMediaListImage,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const dispatch = useDispatch();
  const { displayToast } = useToast();
  const { gridSizeMedium: gridSize } = useGridContainer(true);
  const { loggedInUser } = useGetLoggedInUserDetails();

  const [docUploadDate, setDocUploadDate] = useState<string>("");
  const isNewFileUploadedRef = useRef(false);
  const [clearFile, setClearFile] = useState<boolean>(false);
  const [isCancelWarningVisible, setIsCancelWarningVisible] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const { isMobile } = useMediaQueries();
  const gridSpacing = isMobile ? 2 : 4;

  const {
    mutateAsync: fileUpload,
    isPending: isfileUploadPending,
    isError: isfileUploadError,
  } = useUploadFileMutation();

  const {
    mutateAsync: uploadDocumentDetails,
    isError: isUploadDocumentDetailsError,
    isSuccess: isUploadDocumentDetailsSuccess,
  } = useProgramUploadDocumentDetailsMutation();

  const {
    uploadNewDocumentTitle,
    programDocumentTypeList,
    maximumFileSizeAndType,
    attachmentSection,
    uploadDocument: uploadDocumentLabel,
    description,
    documentDetailsSection,
    type,
    uploadDate: uploadDateLabel,
    maxFileSizeErrorMessage,
    fileFormatErrorMessage,
    documentUploadedToastMessage,
  } = programUploadNewDocumentSitecoreKeys;
  const getLabel = useFetchLabel(uploadNewDocumentTitle);
  const getDropdownList = (key: string) =>
    fetchDropdownValues(uploadNewDocumentTitle, key);
  const uploadImage = fetchMediaListImage(
    commonSiteCoreKeys.commonComponentKey,
    commonSiteCoreKeys.browseImage,
    commonSiteCoreKeys.smallImage,
  );

  const methods = useForm({
    mode: "all",
    defaultValues: {
      [UploadNewDocumentFormKey.documentType]: "",
      [UploadNewDocumentFormKey.description]: "",
      [UploadNewDocumentFormKey.uploadedDate]: "",
    },
    resolver: yupResolver(
      UploadNewDocumentModalValidationSchema(commonValidationMessages),
    ),
  });
  const {
    control,
    reset,
    handleSubmit,
    getValues,
    formState: { dirtyFields },
  } = methods;

  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );

  useEffect(() => {
    if (isfileUploadPending) {
      setIsUploading(isfileUploadPending);
    }
  }, [isfileUploadPending]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isUploadingDocument: {
          isVisible: isUploading,
        },
      }),
    );

    return () => {
      dispatch(
        toggleLoader({
          isUploadingDocument: { isVisible: false },
        }),
      );
    };
  }, [isUploading]);

  useEffect(() => {
    if (isfileUploadError || isUploadDocumentDetailsError) {
      setIsUploading(false);
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater);
    }
  }, [isfileUploadError, isUploadDocumentDetailsError]);

  useEffect(() => {
    if (isUploadDocumentDetailsSuccess) {
      setIsUploading(false);
      displayToast(getLabel(documentUploadedToastMessage));
      onDocumentUploadSuccess();
    }
  }, [isUploadDocumentDetailsSuccess]);

  const handleOnClose = () => {
    if (
      isEmpty(dirtyFields) &&
      isEmpty(getValues(UploadNewDocumentFormKey.file))
    ) {
      onClose();
    } else {
      setIsCancelWarningVisible(true);
    }
  };

  const uploadDocument = async () => {
    const { file } = getValues();
    return await fileUpload({
      documentType: "program",
      file: file,
      operationType: "add",
      entityType: "program",
      entityId: programId,
    });
  };

  const handleOnUpload = async () => {
    const response: FileUploadResponse = await uploadDocument();
    if (!response?.success) return;

    const { documentType, description } = getValues();
    uploadDocumentDetails({
      programId: programId,
      type: documentType,
      blobId: response.documentId,
      fileName: response.fileName,
      ...getFieldUndefinedForPayloadParams({
        description: description,
      }),
    });
  };

  const handleDocumentUpdate = useCallback(
    (date: Date | null) => {
      isNewFileUploadedRef.current = true;
      setDocUploadDate(date ? dayjs(date).format(userCountryDateFormat) : "");
    },
    [userCountryDateFormat],
  );

  const handleDocumentTypeChange = (documentType: string) => {
    if (documentType) {
      reset((formValues) => ({
        ...formValues,
        [UploadNewDocumentFormKey.description]: "",
      }));
      setClearFile(true);
      setTimeout(() => setClearFile(false), 200);
    }
  };

  const browseImageMemo = useMemo(
    () => ({
      src: uploadImage?.ImageUrl ?? "",
      alt: uploadImage?.AltText ?? "",
    }),
    [uploadImage?.ImageUrl, uploadImage?.AltText],
  );

  return (
    <MuiModal
      open={open}
      onClose={handleOnClose}
      title={fetchComponentTitle(uploadNewDocumentTitle)}
      hasLargeContent
      primaryButtonTitle={commonLabels.upload}
      secondaryButtonTitle={commonLabels.cancel}
      onSecondaryButtonClick={handleOnClose}
      onPrimaryButtonClick={handleSubmit(handleOnUpload)}
      className="upload-new-document-modal"
    >
      <FormProvider {...methods}>
        <UploadNewDocumentModalWrapper>
          <AccordionHeader
            title={
              <AccordionHeaderText label={getLabel(documentDetailsSection)} />
            }
            isExpanded={true}
            showExpandCollapseIcon={false}
          >
            <MuiBox className="content separator">
              <MuiGrid container spacing={gridSpacing}>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={UploadNewDocumentFormKey.documentType}
                  >
                    <MuiSelect
                      id={`${pageId}-${UploadNewDocumentFormKey.documentType}`}
                      label={getLabel(type)}
                      placeholder={commonLabels.selectPlaceHolder}
                      options={getDropdownList(programDocumentTypeList)}
                      onChange={(e) => {
                        handleDocumentTypeChange(e.target.value as string);
                      }}
                      isMandatory
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
                <MuiGrid size={gridSize}>
                  <FormController
                    control={control}
                    name={UploadNewDocumentFormKey.uploadedDate}
                  >
                    <MuiTextField
                      id={`${pageId}-${UploadNewDocumentFormKey.uploadedDate}`}
                      label={getLabel(uploadDateLabel)}
                      disabled={true}
                      placeholder={docUploadDate || userCountryDateFormat}
                      className="upload-date"
                      endAdornment={
                        <InputAdornment position="end">
                          <CalendarIcon />
                        </InputAdornment>
                      }
                      requiredARIALabel={commonARIALabel.required}
                    />
                  </FormController>
                </MuiGrid>
              </MuiGrid>

              <MuiBox className="desc-input-box-wrapper">
                <FormController
                  control={control}
                  name={UploadNewDocumentFormKey.description}
                >
                  <MuiTextField
                    id={`${pageId}-${UploadNewDocumentFormKey.description}`}
                    label={getLabel(description)}
                    placeholder={commonLabels.inputPlaceHolder}
                    className="desc-input"
                    multiline
                    rows={4}
                    maxLength={MAX_LENGTH_500}
                    data-testid={`${pageId}-${UploadNewDocumentFormKey.description}`}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiBox>
            </MuiBox>
          </AccordionHeader>
          <MuiBox>
            <AccordionHeader
              title={
                <AccordionHeaderText label={getLabel(attachmentSection)} />
              }
              isExpanded={true}
              showExpandCollapseIcon={false}
            >
              <MuiBox>
                <FormController
                  control={control}
                  name={UploadNewDocumentFormKey.file}
                >
                  <FileUploader
                    label={getLabel(uploadDocumentLabel)}
                    isMandatory
                    onDocumentChange={handleDocumentUpdate}
                    formNameKey={UploadNewDocumentFormKey.file}
                    data-testid={`${pageId}-${UploadNewDocumentFormKey.file}`}
                    clearUploadedFile={clearFile}
                    acceptedFileTypes={getAllFileTypes()}
                    maxFileSizeErrorMessageText={getLabel(
                      maxFileSizeErrorMessage,
                    )}
                    fileFormatErrorMessageText={getLabel(
                      fileFormatErrorMessage,
                    )}
                    sizeLabel={getLabel(maximumFileSizeAndType)}
                    browseLabel={commonLabels.browse}
                    dropYourFileLabel={commonLabels.dropYourFile}
                    browseImage={browseImageMemo}
                  />
                </FormController>
              </MuiBox>
            </AccordionHeader>
          </MuiBox>

          {isCancelWarningVisible && (
            <WarningConfirmationModal
              show={isCancelWarningVisible}
              labelContent={commonLabels.cancelButtonActionText}
              primaryButtonTitle={commonLabels.yes}
              secondaryButtonTitle={commonLabels.no}
              onClose={() => {
                setIsCancelWarningVisible(false);
              }}
              onPrimaryButtonClick={() => {
                setIsCancelWarningVisible(false);
                onClose();
              }}
            />
          )}
        </UploadNewDocumentModalWrapper>
      </FormProvider>
    </MuiModal>
  );
};
